import { useCallback, useRef, useState } from 'react';
import API_CONFIG from '../../../config/api';
import { hasValidRcaPayload } from '../../BugRca/bugRcaUiConfig';
import { getJiraInfo } from '../productKnowledgeUtils';

async function requestRcaGenerate(jiraKey, forceRefresh) {
  const response = await API_CONFIG.request(`/jira/bug-rca/${jiraKey}/generate`, {
    method: 'POST',
    body: JSON.stringify({ force_refresh: Boolean(forceRefresh) }),
  });
  const data = await response.json();
  return { response, data };
}

async function requestRcaGet(jiraKey) {
  const response = await API_CONFIG.request(`/jira/bug-rca/${jiraKey}`, { method: 'GET' });
  if (!response.ok) {
    return { response, data: null };
  }
  const data = await response.json();
  return { response, data };
}

function normalizeRcaError(data, fallbackMessage) {
  return {
    error: data?.message || data?.detail || fallbackMessage,
    unavailable: true,
  };
}

export default function useProductKnowledgeBugRca({ setSnackbar, refreshPrimaryLists }) {
  const cacheRef = useRef({});
  const [rcaModalOpen, setRcaModalOpen] = useState(false);
  const [rcaData, setRcaData] = useState(null);
  const [rcaLoading, setRcaLoading] = useState(false);
  const [rcaGenerating, setRcaGenerating] = useState(false);
  const [rcaIssueKey, setRcaIssueKey] = useState(null);

  const loadRca = useCallback(
    async (jiraKey, { forceRefresh = false, useGenerate = false } = {}) => {
      if (!jiraKey) {
        setSnackbar({ open: true, message: 'JIRA issue key not found', severity: 'error' });
        return;
      }

      setRcaIssueKey(jiraKey);
      setRcaModalOpen(true);

      const cached = cacheRef.current[jiraKey];
      if (!forceRefresh && hasValidRcaPayload(cached)) {
        setRcaData(cached);
      } else {
        if (cached && !hasValidRcaPayload(cached)) {
          delete cacheRef.current[jiraKey];
        }
        setRcaData(null);
      }

      setRcaLoading(true);
      if (forceRefresh || useGenerate) {
        setRcaGenerating(true);
      }

      try {
        let payload = null;

        if (forceRefresh || useGenerate) {
          const { response, data } = await requestRcaGenerate(jiraKey, forceRefresh);
          if (!response.ok || !data?.success) {
            setRcaData(normalizeRcaError(data, 'Failed to generate RCA'));
            return;
          }
          payload = data;
        } else {
          const { response, data } = await requestRcaGet(jiraKey);
          if (response.ok && hasValidRcaPayload(data)) {
            payload = data;
          } else if (response.status === 404 || !hasValidRcaPayload(data)) {
            const generated = await requestRcaGenerate(jiraKey, false);
            if (!generated.response.ok || !generated.data?.success) {
              setRcaData(normalizeRcaError(generated.data, 'Failed to generate RCA'));
              return;
            }
            payload = generated.data;
          } else {
            setRcaData(normalizeRcaError(data, 'Failed to load RCA'));
            return;
          }
        }

        if (!hasValidRcaPayload(payload)) {
          setRcaData({
            error: 'RCA result unavailable',
            unavailable: true,
          });
          return;
        }

        cacheRef.current[jiraKey] = payload;
        setRcaData(payload);

        if (forceRefresh || useGenerate) {
          setSnackbar({
            open: true,
            message: forceRefresh ? 'RCA re-analyzed successfully!' : 'RCA generated successfully!',
            severity: 'success',
          });
          await refreshPrimaryLists?.();
        }
      } catch (err) {
        console.error('Error loading RCA:', err);
        setRcaData({
          error: err.message || 'Failed to load RCA data',
          unavailable: true,
        });
      } finally {
        setRcaLoading(false);
        setRcaGenerating(false);
      }
    },
    [refreshPrimaryLists, setSnackbar]
  );

  const handleOpenRca = useCallback(
    async (item) => {
      const jiraKey = getJiraInfo(item).jiraKey;
      await loadRca(jiraKey, { forceRefresh: false, useGenerate: false });
    },
    [loadRca]
  );

  const handleGenerateRca = useCallback(
    async (item) => {
      const jiraKey = getJiraInfo(item).jiraKey;
      await loadRca(jiraKey, { forceRefresh: false, useGenerate: true });
    },
    [loadRca]
  );

  const handleRefreshRca = useCallback(
    async (forceRefresh = true) => {
      if (!rcaIssueKey) return;
      await loadRca(rcaIssueKey, { forceRefresh, useGenerate: true });
    },
    [loadRca, rcaIssueKey]
  );

  const closeRcaModal = useCallback(() => {
    setRcaModalOpen(false);
    setRcaIssueKey(null);
    setRcaData(null);
    setRcaLoading(false);
    setRcaGenerating(false);
  }, []);

  return {
    rcaModalOpen,
    rcaData,
    rcaLoading,
    rcaGenerating,
    rcaIssueKey,
    handleOpenRca,
    handleGenerateRca,
    handleRefreshRca,
    closeRcaModal,
  };
}
